// CALLBACK STRIP — the callback-time chip on lead / daily-request cards and rows.
//
// WHY THIS FILE EXISTS (owner brief 2026-10-26). The four strips in App.js (Leads
// mobile card, Leads desktop Callback cell, Daily Request mobile card, Daily
// Request desktop cell) painted a raw "YYYY-MM-DD HH:MM" over one of three
// colours and never ticked, so a strip went red only when something else
// re-rendered the row, and "red" said nothing about HOW late the call was. This
// module owns the wording and the three overdue levels; App.js keeps the
// decision of WHETHER a strip renders at all (callbackDisplayMode: a closed
// record renders none, a Not Interested record stays neutral grey — both are
// passed in, never re-derived here).
//
// THRESHOLDS ARE THE BELL'S. CallbackBell (App.js, cbWindows) is the source of
// truth for overdue / now / upcoming: "now" is ±30 minutes around the scheduled
// time, "overdue" starts 30 minutes PAST it, not at the time itself. The strip
// reads the same constant, so a call the bell still files under Now is never
// painted overdue here. The three overdue levels (under 1 h, 1–24 h, 24 h+) are
// the strip's own and only split what the bell calls overdue.
//
// STORAGE CONTRACT, unchanged: callbackTime is a NAIVE-LOCAL "YYYY-MM-DDTHH:MM"
// string, byte-identical to what <input type="datetime-local"> emits. It is
// parsed here the way callbackColor and the bell already parse it — as browser
// local time — and the reschedule sheet emits the same shape, so the save path
// and the server's lexicographic range comparisons are untouched.
//
// Everything that decides wording, colour or order is a pure module-scope
// function of (callbackTime, t, nowMs) so a gate can RUN it (CLAUDE.md: a rule
// inside a render closure cannot be gated).
import React, { useState, useEffect } from "react";
import { fmtWallTime12, fmtWallDateTime12 } from "./timeFormat";

// Same value as CallbackBell's NOW_DELAY_MS / NOW_UPCOMING_MS. Read, not copied
// by hand — verify-callback-strip.js asserts the two literals are equal.
export var CB_NOW_WINDOW_MS = 30 * 60 * 1000;
export var CB_HOUR_MS = 60 * 60 * 1000;
export var CB_DAY_MS = 24 * CB_HOUR_MS;

// Strip palette by level. 0 = upcoming / now (the green the strip always had).
export var CB_LEVEL_STYLE = {
  0: { bg: "#DCFCE7", color: "#15803D", border: null },
  1: { bg: "#FCEBEB", color: "#791F1F", border: "#F2B4B4" },
  2: { bg: "#F7C1C1", color: "#591C1C", border: "#C0392B" },
  3: { bg: "#A32D2D", color: "#FFFFFF", border: "#A32D2D" }
};
export var CB_NEUTRAL_STYLE = { bg: "#F8FAFC", color: "#64748B", border: null };

// THE DESKTOP COLUMN IS PINNED (owner brief 2026-10-27, PHONE_COL_PIN doctrine):
// width = minWidth = maxWidth on the <th> AND the <td>, so neither the 60 s tick
// (a pill moving from "in 20 min" to "Now") nor a hover can change the column's
// width, and every pill in the column is the SAME box — 100% of the cell, 26px
// tall, text centred, nowrap + ellipsis. 150 clears the widest compact label
// ("📞 Tomorrow 3:00 PM", "⚠ Overdue · 59 min") inside the cell's 12px side padding.
export var CB_COL_W = 150;
export var CB_COL_PIN = { width: CB_COL_W, minWidth: CB_COL_W, maxWidth: CB_COL_W, boxSizing: "border-box", whiteSpace: "nowrap" };
export var CB_PILL_H = 26;

var pad2 = function (n) { return n < 10 ? "0" + n : "" + n; };
// Naive-local "YYYY-MM-DDTHH:MM" from a Date — the datetime-local wire format.
export var toNaiveLocal = function (d) {
  return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate()) + "T" + pad2(d.getHours()) + ":" + pad2(d.getMinutes());
};
var localDayKey = function (ms) {
  var d = new Date(ms);
  return d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
};

export var parseCallbackTime = function (str) {
  if (!str || typeof str !== "string") return NaN;
  return new Date(str.slice(0, 16)).getTime();
};

// ---- Arabic (Egyptian) count words. There was no shared plural helper in src/
// before this file; timeAgo renders the singular unit for every count. ----
var AR_UNITS = {
  minute: { one: "دقيقة", two: "دقيقتين", few: "دقايق", many: "دقيقة" },
  hour:   { one: "ساعة",  two: "ساعتين",  few: "ساعات",  many: "ساعة" },
  day:    { one: "يوم",   two: "يومين",   few: "أيام",   many: "يوم" }
};
var EN_UNITS = {
  minute: { one: "min", other: "min" },
  hour:   { one: "hr",  other: "hrs" },
  day:    { one: "day", other: "days" }
};
// arCount(3, "hour") -> "3 ساعات"; arCount(1, "day") -> "يوم"; arCount(2, "day") -> "يومين".
export var arCount = function (n, unit) {
  var u = AR_UNITS[unit];
  n = Math.max(0, Math.round(n));
  if (n === 1) return u.one;
  if (n === 2) return u.two;
  if (n >= 3 && n <= 10) return n + " " + u.few;
  return n + " " + u.many;
};
export var enCount = function (n, unit) {
  var u = EN_UNITS[unit];
  n = Math.max(0, Math.round(n));
  return n + " " + (n === 1 ? u.one : u.other);
};
export var countWord = function (n, unit, isAr) { return isAr ? arCount(n, unit) : enCount(n, unit); };
// Desktop-column units: "20 min" / "3 h" / "2 d" (Arabic keeps its count words).
var SHORT_UNITS = { minute: "min", hour: "h", day: "d" };
export var shortCount = function (n, unit, isAr) { return isAr ? arCount(n, unit) : Math.max(0, Math.round(n)) + " " + SHORT_UNITS[unit]; };

// The biggest whole unit for an elapsed span: minutes under an hour, hours under
// a day, days after. Floors, so "59 min" never reads "1 hr".
var spanParts = function (ms) {
  if (ms < CB_HOUR_MS) return { n: Math.max(1, Math.floor(ms / 60000)), unit: "minute" };
  if (ms < CB_DAY_MS) return { n: Math.floor(ms / CB_HOUR_MS), unit: "hour" };
  return { n: Math.floor(ms / CB_DAY_MS), unit: "day" };
};

// Level of a callback at nowMs. null when there is no parseable time.
//   kind: "upcoming" | "now" | "overdue"; level: 0 for upcoming/now, 1..3 overdue.
export var callbackLevel = function (cbTime, nowMs) {
  var ts = parseCallbackTime(cbTime);
  if (isNaN(ts)) return null;
  var diff = ts - nowMs;
  if (diff >= -CB_NOW_WINDOW_MS && diff <= CB_NOW_WINDOW_MS) return { kind: "now", level: 0, ts: ts, diffMs: diff };
  if (diff > 0) return { kind: "upcoming", level: 0, ts: ts, diffMs: diff };
  var late = -diff;
  var level = late >= CB_DAY_MS ? 3 : late >= CB_HOUR_MS ? 2 : 1;
  return { kind: "overdue", level: level, ts: ts, diffMs: diff };
};

// What the strip shows. Pure. `t` is the page's translation table (t.dir tells
// the language); `neutral` is callbackDisplayMode(record) === "neutral", decided
// by the caller. Returns null when there is nothing to paint.
// `compact` (owner brief 2026-10-27) is the DESKTOP column's wording — the
// mobile card keeps the original. Overdue reads "⚠ Overdue · 3 h" / "⚠ Overdue
// · 2 d" at every level (the original time moves to `title`, a tooltip);
// upcoming reads "📞 in 20 min" / "📞 Today 3:00 PM" / "📞 Tomorrow 3:00 PM" /
// "📞 02/11 1:25 PM". Level colours are untouched. The clock is 12-hour via
// fmtWallTime12 (src/lib/timeFormat.js) — the stored naive string is read as
// digits, never through new Date().
export var callbackStripModel = function (cbTime, t, nowMs, neutral, compact) {
  var lv = callbackLevel(cbTime, nowMs);
  if (!lv) return null;
  var isAr = !!(t && t.dir === "rtl");
  var hhmm = fmtWallTime12(cbTime, t);
  var ddmm = String(cbTime).slice(8, 10) + "/" + String(cbTime).slice(5, 7);
  var title = ddmm + " " + hhmm;
  if (neutral) {
    return { kind: "neutral", level: 0, label: "📞 " + ddmm + " " + hhmm, muted: null, title: title, style: CB_NEUTRAL_STYLE, reschedule: false };
  }
  var tr = function (k, fallback) { return (t && t[k]) || fallback; };
  var label, muted = null;
  if (lv.kind === "overdue" && compact) {
    var spc = spanParts(-lv.diffMs);
    label = "⚠ " + (isAr ? "اتأخر" : "Overdue") + " · " + shortCount(spc.n, spc.unit, isAr);
  } else if (lv.kind === "overdue") {
    var sp = spanParts(-lv.diffMs);
    if (lv.level === 3) {
      label = "🔴 " + (isAr ? tr("cbLateSince", "فات من") + " " + arCount(sp.n, "day")
                            : enCount(sp.n, "day") + " " + tr("cbLateSince", "late"));
    } else {
      label = "⚠️ " + tr("cbOverdueBy", isAr ? "معاد المكالمة اتأخر" : "Callback overdue") + " · " + countWord(sp.n, sp.unit, isAr);
      muted = hhmm;
    }
  } else if (lv.kind === "now" && lv.diffMs <= 0) {
    label = "📞 " + tr("cbNow", isAr ? "دلوقتي" : "Now") + " · " + hhmm;
  } else if (lv.diffMs < 2 * CB_HOUR_MS) {
    var ap = spanParts(lv.diffMs);
    label = "📞 " + tr("cbIn", isAr ? "كمان" : "in") + " " + (compact ? shortCount(ap.n, ap.unit, isAr) : countWord(ap.n, ap.unit, isAr));
  } else {
    var today = localDayKey(nowMs), tomorrow = localDayKey(nowMs + CB_DAY_MS), day = String(cbTime).slice(0, 10);
    if (day === today) label = "📞 " + tr("cbToday", isAr ? "النهاردة" : "Today") + " " + hhmm;
    else if (day === tomorrow) label = "📞 " + tr("cbTomorrow", isAr ? "بكرة" : "Tomorrow") + " " + hhmm;
    else label = "📞 " + ddmm + " " + hhmm;
  }
  return { kind: lv.kind, level: lv.level, label: label, muted: muted, title: title, style: CB_LEVEL_STYLE[lv.level], reschedule: lv.level === 3 };
};

// ---- Sort: level 3, then 2, then 1, then now/upcoming by time; no callback last.
// `modeOf(record)` is callbackDisplayMode from App.js (passed in, never copied):
// a hidden record ranks as "no callback"; a neutral one sorts after upcoming.
// NOT wired to a picker yet — see the 2026-10-26 report: a picker value must be
// a server sort key (verify-receipt-contract.js), and LIST_SORT_SPECS has none.
export var callbackSortRank = function (record, nowMs, modeOf) {
  var mode = modeOf ? modeOf(record) : "normal";
  if (!record || !record.callbackTime || mode === "hide") return 5;
  var lv = callbackLevel(record.callbackTime, nowMs);
  if (!lv) return 5;
  if (mode === "neutral") return 4;
  if (lv.level === 3) return 0;
  if (lv.level === 2) return 1;
  if (lv.level === 1) return 2;
  return 3;
};
export var compareByCallback = function (a, b, nowMs, modeOf) {
  var ra = callbackSortRank(a, nowMs, modeOf), rb = callbackSortRank(b, nowMs, modeOf);
  if (ra !== rb) return ra - rb;
  if (ra === 5) return 0;
  return parseCallbackTime(a.callbackTime) - parseCallbackTime(b.callbackTime);
};

// One clock per page: re-renders every 60 s so the strips move between levels
// with no refresh. Rows read it through props; no interval per row.
export var useMinuteTick = function () {
  var [now, setNow] = useState(Date.now());
  useEffect(function () {
    var id = setInterval(function () { setNow(Date.now()); }, 60000);
    return function () { clearInterval(id); };
  }, []);
  return now;
};

// ---- The strip. `model` comes from callbackStripModel (the caller already ran
// callbackDisplayMode, so passing the model keeps one computation per row).
//   variant "card": block strip on the mobile card.
//   variant "cell": inline pill in the desktop Callback column.
// onReschedule opens the sheet; the button stops propagation so the card's own
// onClick (open the side panel) does not fire.
export var CallbackStrip = function (p) {
  var m = p.model;
  if (!m) return null;
  var isAr = !!(p.t && p.t.dir === "rtl");
  var card = p.variant !== "cell";
  var s = m.style;
  var btn = m.reschedule && p.onReschedule ? <button type="button"
      onClick={function (e) { e.stopPropagation(); p.onReschedule(); }}
      style={{ background: "#fff", color: CB_LEVEL_STYLE[3].bg, border: "none", borderRadius: 999, padding: card ? "5px 12px" : "2px 8px", fontSize: card ? 12 : 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
      {(p.t && p.t.cbReschedule) || (isAr ? "أجّل المكالمة" : "Reschedule")}
    </button> : null;
  var mutedNode = m.muted ? <span style={{ opacity: 0.65, fontWeight: 500, marginInlineStart: 6 }}>{m.muted}</span> : null;
  if (card) {
    return <div data-cb-level={m.level} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 11, fontWeight: 600, color: s.color, marginBottom: 8, padding: btn ? "4px 6px 4px 10px" : "4px 10px", background: s.bg, borderRadius: 8 }}>
      <span style={{ minWidth: 0 }}>{m.label}{mutedNode}</span>
      {btn}
    </div>;
  }
  // DESKTOP CELL (owner brief 2026-10-27): every pill is the same box — the
  // whole cell wide, CB_PILL_H tall, text centred, nowrap + ellipsis — so the
  // column never moves on hover or on the 60 s tick. The original time is the
  // tooltip. A 24 h+ pill has no room for an inline button: the pill itself
  // opens the Reschedule sheet (cursor + title say so), same handler as the card.
  var clickable = !!(m.reschedule && p.onReschedule);
  var isArCell = !!(p.t && p.t.dir === "rtl");
  var cellTitle = clickable ? (m.title || "") + " · " + ((p.t && p.t.cbReschedule) || (isArCell ? "أجّل المكالمة" : "Reschedule")) : (m.title || "");
  return <span data-cb-level={m.level} title={cellTitle} role={clickable ? "button" : undefined}
      onClick={clickable ? function (e) { e.stopPropagation(); p.onReschedule(); } : undefined}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: CB_PILL_H, boxSizing: "border-box", padding: "0 8px", borderRadius: 13, background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", cursor: clickable ? "pointer" : "default" }}>
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}{mutedNode}</span>
  </span>;
};

// ---- Quick picks for the reschedule sheet. Naive-local strings, same as
// QuickCbButtons in App.js. `current` is the record's stored callbackTime, used
// for "tomorrow, same time".
export var rescheduleQuickPicks = function (nowMs, current, t) {
  var isAr = !!(t && t.dir === "rtl");
  var tr = function (k, fallback) { return (t && t[k]) || fallback; };
  var inHour = new Date(nowMs); inHour.setHours(inHour.getHours() + 1);
  var tmrw10 = new Date(nowMs); tmrw10.setDate(tmrw10.getDate() + 1); tmrw10.setHours(10, 0, 0, 0);
  var cur = String(current || "");
  var hh = Number(cur.slice(11, 13)), mm = Number(cur.slice(14, 16));
  var tmrwSame = new Date(nowMs); tmrwSame.setDate(tmrwSame.getDate() + 1);
  tmrwSame.setHours(isNaN(hh) ? 10 : hh, isNaN(mm) ? 0 : mm, 0, 0);
  return [
    { key: "hour",     label: tr("cbQuickHour", isAr ? "بعد ساعة" : "In 1 hour"),                         value: toNaiveLocal(inHour) },
    { key: "tmrw10",   label: tr("cbQuickTomorrowMorning", isAr ? "بكرة الصبح 10:00 ص" : "Tomorrow 10:00 AM"),  value: toNaiveLocal(tmrw10) },
    { key: "tmrwSame", label: tr("cbQuickTomorrowSame", isAr ? "بكرة نفس الوقت" : "Tomorrow, same time"), value: toNaiveLocal(tmrwSame) }
  ];
};

// The sheet. Position:fixed like the status menu (data-overlay-above), so it is
// clipped by nothing and adds no scroll container to the page. Saving hands the
// chosen naive-local string to onSave; the PAGE writes it through its own
// confirmStatus (same PUT, same activity row, same audit as the status modal).
export var RescheduleSheet = function (p) {
  var [val, setVal] = useState("");
  useEffect(function () { setVal(""); }, [p.open]);
  if (!p.open) return null;
  var isAr = !!(p.t && p.t.dir === "rtl");
  var tr = function (k, fallback) { return (p.t && p.t[k]) || fallback; };
  var picks = rescheduleQuickPicks(p.nowMs || Date.now(), p.current, p.t);
  var save = function () { if (!val || p.busy) return; p.onSave(val); };
  return <div data-overlay-above="true" onClick={function () { if (!p.busy) p.onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
    <div onClick={function (e) { e.stopPropagation(); }} dir={isAr ? "rtl" : "ltr"}
        style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: "16px 16px 0 0", padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))", boxSizing: "border-box", boxShadow: "0 -8px 32px rgba(0,0,0,0.18)" }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#1E293B", marginBottom: 4 }}>📞 {tr("cbReschedule", isAr ? "أجّل المكالمة" : "Reschedule")}</div>
      {p.current && <div style={{ fontSize: 11, color: "#64748B", marginBottom: 12 }}>{tr("cbWasAt", isAr ? "كان معاده" : "Was at")} {fmtWallDateTime12(p.current, p.t)}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {picks.map(function (q) {
          var on = val === q.value;
          return <button key={q.key} type="button" onClick={function () { setVal(q.value); }}
            style={{ flex: "1 1 0", minWidth: 100, padding: "9px 10px", borderRadius: 10, border: "1px solid " + (on ? "#3B82F6" : "#E2E8F0"), background: on ? "#EFF6FF" : "#fff", color: on ? "#1D4ED8" : "#334155", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{q.label}</button>;
        })}
      </div>
      <input type="datetime-local" value={val} onChange={function (e) { setVal(e.target.value); }}
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 14, boxSizing: "border-box", marginBottom: 12 }}/>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={function () { if (!p.busy) p.onClose(); }}
          style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1px solid #E2E8F0", background: "#fff", color: "#334155", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{tr("cancel", "Cancel")}</button>
        <button type="button" onClick={save} disabled={!val || !!p.busy}
          style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: (!val || p.busy) ? "#CBD5E1" : "#1a2942", color: "#fff", fontSize: 13, fontWeight: 700, cursor: (!val || p.busy) ? "default" : "pointer" }}>{p.busy ? "…" : tr("save", "Save")}</button>
      </div>
    </div>
  </div>;
};
